import { useCallback } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import token_icon from "assets/token.svg";
import useModalState from "hooks/useModalState";
import BuyTokensModal from "modals/BuyTokensModal";
import { useMappedState } from "redux-react-hook";
import styled from "styled-components";

interface IBuyTokenButtonProps {
  onPremiumClick: () => void;
  onClick?: () => void;
  isMobile?: boolean;
  isChip?: boolean;
  label?: string;
  showBetaBadge?: boolean;
}

const BuyTokenButton: React.FC<IBuyTokenButtonProps> = (props) => {
  const { onPremiumClick, isMobile, isChip, showBetaBadge = true } = props;
  const [isBuyTokensModalOpen, openBuyTokensModal, closeBuyTokensModal] = useModalState();

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        hasSubscription: hasSubscription
      };
    }, [])
  );

  const tokenBalance = currentUser?.tokenBalance ?? 0.0;

  const handleBuyTokenButtonClick = () => {
    if (!hasSubscription) {
      onPremiumClick();
      return;
    }
    openBuyTokensModal();
  };


  const mobileSideNavButton = () => {
    return (
      <MobileButtonContainer>
        <TokenMobileWrapper onClick={handleBuyTokenButtonClick}>
          <TokenIcon src={token_icon} alt="Token" />
          {tokenBalance}
          <PlusIcon width={18} height={18} />
        </TokenMobileWrapper>
        {showBetaBadge && <MobileBetaBadge onClick={handleBuyTokenButtonClick}>BETA</MobileBetaBadge>}
      </MobileButtonContainer>
    );
  };

  const desktopBuyTokenButton = () => {
    return (
      <ButtonContainer>
        <Button onClick={handleBuyTokenButtonClick}>
          <TokenIcon src={token_icon} alt="Token" />
          {tokenBalance}
          <PlusIcon width={24} height={24} />
        </Button>
        {showBetaBadge && <BetaBadge onClick={handleBuyTokenButtonClick}>BETA</BetaBadge>}
      </ButtonContainer>
    );
  };

  const chipButton = () => {
    return (
      <ChipWrapper>
        <TokenIcon src={token_icon} alt="Token" />
        <ChipTokenBalance>
          {tokenBalance}
        </ChipTokenBalance>
      </ChipWrapper>
    );
  };

  return (
    <>
      {isChip && chipButton()}
      {isMobile && !isChip && mobileSideNavButton()}
      {!isMobile && !isChip && desktopBuyTokenButton()}
      {isBuyTokensModalOpen && <BuyTokensModal isOpen onClose={closeBuyTokensModal} analyticsDataRenderReason='buy_token_button_click' />}
    </>
  );
};

export default BuyTokenButton;

const baseButtonStyles = `
  display: flex;
  align-items: center;
  cursor: pointer;
  color: white;
  font-weight: 700;
  border: 1px solid #282828;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #282828;
    
    img {
      transform: rotateY(360deg);
    }
  }
`;

const TokenIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  transition: transform 0.5s ease-in-out;

  @media (max-width: 600px) {
    width: 19px;
    height: 19px;
  }
`;

const ButtonContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 16px;
`;

const Button = styled.div`
  ${baseButtonStyles}
  font-size: 18px;
  height: 48px;
  border-radius: 12px;
  padding: 0 12px;
`;

const BetaBadge = styled.div`
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #FF7E5F;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 0px 4px;
  cursor: pointer;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TokenMobileWrapper = styled.div`
  ${baseButtonStyles}
  font-size: 12px;
  padding: 4px 6px;
  border-radius: 10px;
`;

const ChipWrapper = styled.div`
  ${baseButtonStyles}
  font-size: 16px;
  padding: 4px 6px;
  border-radius: 24px;
  background: rgba(40, 40, 40, 0.5);
  display: inline-flex;
  max-height: 18px;

  img {
    width: 10px;
    height: 10px;
    margin-right: 6px;
  }
`;

const ChipTokenBalance = styled.div`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 10px;
  line-height: 100%;
`;

const MobileButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MobileBetaBadge = styled.div`
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #FF7E5F;
  color: white;
  font-size: 6px;
  font-weight: 700;
  padding: 1px 3px;
  cursor: pointer;
  border-radius: 2px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1;
`;
